/**
 * Warteschlange für Fotos und Dateien, die wegen fehlender Verbindung nicht gleich hochgeladen werden konnten.
 * Sie liegt im Speicher des Geräts (IndexedDB) und wird automatisch abgearbeitet, sobald wieder Netz da ist.
 * Dieselbe Datenbank nutzt auch der Service Worker (public/sw.js) für „Teilen an HandVero“ („inbox“).
 */
import type { FileInfo } from "@handvero/shared";
import { ApiFailure, upload } from "../api";

const DB_NAME = "hv-queue";
const DB_VERSION = 2;
export const QUEUE_EVENT = "hv-queue-changed";

export interface QueuedUpload {
  id?: number;
  folderId: string;
  projectId: string;
  projectName: string;
  folderPath: string;
  name: string;
  file: Blob;
  thumb?: Blob;
  createdAt: number;
  error?: string;
}

export interface SharedItem {
  id?: number;
  name: string;
  type: string;
  blob: Blob;
  createdAt: number;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("Kein Gerätespeicher verfügbar."));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("uploads")) db.createObjectStore("uploads", { keyPath: "id", autoIncrement: true });
      if (!db.objectStoreNames.contains("inbox")) db.createObjectStore("inbox", { keyPath: "id", autoIncrement: true });
      if (!db.objectStoreNames.contains("outbox")) db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Gerätespeicher nicht lesbar."));
  });
}

async function run<T>(store: "uploads" | "inbox" | "outbox", mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error ?? new Error("Gerätespeicher nicht beschreibbar."));
      tx.onabort = () => reject(tx.error ?? new Error("Gerätespeicher voll."));
    });
  } finally {
    db.close();
  }
}

function changed() {
  window.dispatchEvent(new Event(QUEUE_EVENT));
}

export async function queueUpload(item: Omit<QueuedUpload, "id" | "createdAt">): Promise<void> {
  await run("uploads", "readwrite", (s) => s.add({ ...item, createdAt: Date.now() }));
  changed();
}

export const listUploads = () => run<QueuedUpload[]>("uploads", "readonly", (s) => s.getAll()).catch(() => [] as QueuedUpload[]);

export async function removeUpload(id: number): Promise<void> {
  await run("uploads", "readwrite", (s) => s.delete(id));
  changed();
}

export const listInbox = () => run<SharedItem[]>("inbox", "readonly", (s) => s.getAll()).catch(() => [] as SharedItem[]);

export async function clearInbox(): Promise<void> {
  await run("inbox", "readwrite", (s) => s.clear());
}

export function isOffline(err: unknown): boolean {
  return err instanceof ApiFailure && err.status === 0;
}

let flushing = false;

/**
 * Wartende Dateien nacheinander hochladen. Bricht bei fehlender Verbindung ab (bleibt dann liegen).
 * Dauerhaft abgelehnte Dateien (z. B. Speicher voll) bleiben mit Hinweis in der Liste, damit nichts stillschweigend verschwindet.
 */
export async function flushUploads(): Promise<{ sent: number; left: number; unauthorized: boolean }> {
  if (flushing) return { sent: 0, left: (await listUploads()).length + (await listOutbox()).length, unauthorized: false };
  flushing = true;
  let sent = 0;
  let unauthorized = false;
  try {
    const o = await flushOutbox();
    sent += o.sent;
    unauthorized = o.unauthorized;
    for (const item of unauthorized ? [] : await listUploads()) {
      if (item.error) continue;
      const form = new FormData();
      if (item.thumb) form.append("thumb", item.thumb, "vorschau.jpg");
      form.append("file", item.file, item.name);
      try {
        await upload<FileInfo>(`/api/folders/${item.folderId}/files`, form);
        await removeUpload(item.id!);
        sent++;
      } catch (err) {
        if (isOffline(err)) break;
        if (err instanceof ApiFailure && err.status === 401) {
          unauthorized = true;
          break;
        }
        const msg = err instanceof Error ? err.message : "Das Hochladen hat nicht geklappt.";
        // Ordner gelöscht oder Speicher voll: nicht endlos wiederholen.
        await run("uploads", "readwrite", (s) => s.put({ ...item, error: msg }));
        changed();
      }
    }
  } finally {
    flushing = false;
  }
  const left = (await listUploads()).length + (await listOutbox()).filter((r) => !r.error).length;
  if (sent) changed();
  return { sent, left, unauthorized };
}

/* ---------- Eingaben ohne Netz (Notizen, Protokoll-Felder) ---------- */

export interface QueuedRequest {
  id?: number;
  method: "POST" | "PATCH" | "PUT";
  url: string;
  body: Record<string, unknown>;
  /** Anzeigename in der Hinweisleiste, z. B. „Notiz“. */
  label: string;
  createdAt: number;
  error?: string;
}

export const listOutbox = () => run<QueuedRequest[]>("outbox", "readonly", (s) => s.getAll()).catch(() => [] as QueuedRequest[]);

export async function removeRequest(id: number): Promise<void> {
  await run("outbox", "readwrite", (s) => s.delete(id));
  changed();
}

/**
 * Eine Eingabe auf dem Gerät vormerken. Mit `merge` werden mehrere Änderungen am selben Beleg zu einer zusammengefasst
 * (Protokoll: neuere Feldwerte überschreiben ältere, alles andere bleibt).
 */
export async function queueRequest(item: Omit<QueuedRequest, "id" | "createdAt" | "error">, merge = false): Promise<void> {
  if (merge) {
    const old = (await listOutbox()).find((r) => r.url === item.url && r.method === item.method && !r.error);
    if (old) {
      const oldData = (old.body.data ?? {}) as Record<string, unknown>;
      const newData = (item.body.data ?? {}) as Record<string, unknown>;
      await run("outbox", "readwrite", (s) => s.put({ ...old, body: { ...old.body, ...item.body, data: { ...oldData, ...newData } } }));
      changed();
      return;
    }
  }
  await run("outbox", "readwrite", (s) => s.add({ ...item, createdAt: Date.now() }));
  changed();
}

async function flushOutbox(): Promise<{ sent: number; unauthorized: boolean }> {
  let sent = 0;
  for (const item of await listOutbox()) {
    if (item.error) continue;
    let res: Response;
    try {
      res = await fetch(item.url, { method: item.method, credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(item.body) });
    } catch {
      break; // weiter ohne Netz
    }
    if (res.ok) {
      await removeRequest(item.id!);
      sent++;
    } else if (res.status === 401) {
      return { sent, unauthorized: true };
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      await run("outbox", "readwrite", (s) => s.put({ ...item, error: data.error ?? "Der Server hat die Eingabe nicht angenommen." }));
      changed();
    }
  }
  return { sent, unauthorized: false };
}
